/**
 * Onboarding Store (Pinia)
 *
 * Drives the guided coachmark tour for new users. Tour completion and the current
 * step are persisted server-side via the user's preferences (`onboarding_done` /
 * `onboarding_step`), so progress follows the user across devices. The separate
 * getting-started checklist (overrides + dismissal) stays in localStorage, as the
 * backend doesn't model it.
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import {
  ONBOARDING_TOUR,
  CHECKLIST_OVERRIDES_KEY,
  CHECKLIST_DISMISSED_KEY,
  type TourStep,
} from '../config/onboardingTour'
import { useAuthStore } from './auth'

type UserId = string | number | null | undefined

/** Per-user localStorage keys for the (still client-side) checklist. */
const overridesKey = (userId?: UserId) => `${CHECKLIST_OVERRIDES_KEY}:${userId ?? 'anon'}`
const dismissedKey = (userId?: UserId) => `${CHECKLIST_DISMISSED_KEY}:${userId ?? 'anon'}`

export const useOnboardingStore = defineStore('onboarding', () => {
  const authStore = useAuthStore()

  const steps = ref<TourStep[]>(ONBOARDING_TOUR)
  const currentIndex = ref(0)
  const isActive = ref(false)

  const currentStep = computed<TourStep | null>(() => steps.value[currentIndex.value] ?? null)
  const totalSteps = computed(() => steps.value.length)
  const isFirst = computed(() => currentIndex.value === 0)
  const isLast = computed(() => currentIndex.value === steps.value.length - 1)

  const clampIndex = (i: number) => Math.max(0, Math.min(i, steps.value.length - 1))

  /** Fire-and-forget persistence of onboarding progress to the backend. */
  const persistProgress = (data: { onboarding_step?: number; onboarding_done?: boolean }) => {
    void authStore.updateOnboarding(data)
  }

  /** Whether this user has already finished or dismissed the tour (server-side). */
  const hasCompleted = (): boolean => authStore.preferences?.onboarding_done === true

  /** Begin the tour, optionally resuming from a saved step. */
  const start = (fromStep = 0) => {
    currentIndex.value = clampIndex(fromStep)
    isActive.value = true
  }

  /** Begin the tour focused on a specific step (used by the checklist "Show me"). */
  const startAtStep = (stepId: string) => {
    const idx = steps.value.findIndex((s) => s.id === stepId)
    currentIndex.value = idx >= 0 ? idx : 0
    isActive.value = true
    persistProgress({ onboarding_step: currentIndex.value })
  }

  /**
   * Start the tour only if the user hasn't seen it yet, resuming from their saved
   * step. Requires preferences to be loaded so we don't wrongly start for someone
   * who already finished. Returns whether it started.
   */
  const maybeAutoStart = (): boolean => {
    if (isActive.value || !authStore.preferences || hasCompleted()) return false
    start(authStore.preferences.onboarding_step ?? 0)
    return true
  }

  const next = () => {
    if (isLast.value) {
      finish()
      return
    }
    currentIndex.value++
    persistProgress({ onboarding_step: currentIndex.value })
  }

  const prev = () => {
    if (currentIndex.value > 0) {
      currentIndex.value--
      persistProgress({ onboarding_step: currentIndex.value })
    }
  }

  const goTo = (index: number) => {
    if (index >= 0 && index < steps.value.length) currentIndex.value = index
  }

  /** Finish the tour (reached the end) and record completion. */
  const finish = () => {
    isActive.value = false
    persistProgress({ onboarding_done: true, onboarding_step: currentIndex.value })
  }

  /** Dismiss the tour early ("Skip"). Treated the same as finishing for persistence. */
  const dismiss = () => {
    isActive.value = false
    persistProgress({ onboarding_done: true, onboarding_step: currentIndex.value })
  }

  /** Replay: clear completion server-side and restart from the first step. */
  const replay = () => {
    persistProgress({ onboarding_done: false, onboarding_step: 0 })
    start(0)
  }

  // ── Checklist state ─────────────────────────────────────────────────────────
  /** UI: whether the floating checklist is expanded. */
  const checklistExpanded = ref(false)
  /** Whether the user has dismissed the checklist for good. */
  const checklistDismissed = ref(false)
  /** Manual tick/untick overrides, keyed by task id. Wins over auto-detection. */
  const taskOverrides = ref<Record<string, boolean>>({})
  /** Tracks which user's checklist state is currently loaded. */
  const loadedFor = ref<UserId>(undefined)

  /** Load persisted checklist state for a user (idempotent per user). */
  const initChecklist = (userId?: UserId) => {
    if (loadedFor.value === userId) return
    loadedFor.value = userId
    try {
      checklistDismissed.value = localStorage.getItem(dismissedKey(userId)) !== null
      const raw = localStorage.getItem(overridesKey(userId))
      taskOverrides.value = raw ? (JSON.parse(raw) as Record<string, boolean>) : {}
    } catch {
      checklistDismissed.value = false
      taskOverrides.value = {}
    }
  }

  const persistOverrides = (userId?: UserId) => {
    try {
      localStorage.setItem(overridesKey(userId), JSON.stringify(taskOverrides.value))
    } catch {
      /* ignore */
    }
  }

  /** Manually set a task's done state (override auto-detection). */
  const setTaskOverride = (userId: UserId, taskId: string, done: boolean) => {
    taskOverrides.value = { ...taskOverrides.value, [taskId]: done }
    persistOverrides(userId)
  }

  const dismissChecklist = (userId?: UserId) => {
    checklistDismissed.value = true
    checklistExpanded.value = false
    try {
      localStorage.setItem(dismissedKey(userId), new Date().toISOString())
    } catch {
      /* ignore */
    }
  }

  /** Bring the checklist back after it was dismissed (e.g. from the Help page). */
  const restoreChecklist = (userId?: UserId) => {
    checklistDismissed.value = false
    try {
      localStorage.removeItem(dismissedKey(userId))
    } catch {
      /* ignore */
    }
  }

  const toggleChecklist = () => {
    checklistExpanded.value = !checklistExpanded.value
  }

  return {
    steps,
    currentIndex,
    isActive,
    currentStep,
    totalSteps,
    isFirst,
    isLast,
    hasCompleted,
    start,
    startAtStep,
    maybeAutoStart,
    next,
    prev,
    goTo,
    finish,
    dismiss,
    replay,
    // checklist
    checklistExpanded,
    checklistDismissed,
    taskOverrides,
    initChecklist,
    setTaskOverride,
    dismissChecklist,
    restoreChecklist,
    toggleChecklist,
  }
})
